// Currency to locale mapping utility for PaymentSuccess component

/**
 * Currency to locale mapping object
 * Maps ISO 4217 currency codes to BCP 47 language tags
 */
const CURRENCY_LOCALE_MAP = {
    // Major currencies
    USD: 'en-US', // US Dollar
    EUR: 'de-DE', // Euro (German locale)
    GBP: 'en-GB', // British Pound
    JPY: 'ja-JP', // Japanese Yen
    CNY: 'zh-CN', // Chinese Yuan
    INR: 'en-IN', // Indian Rupee
    IDR: 'id-ID', // Indonesian Rupiah

    // European currencies
    CHF: 'de-CH', // Swiss Franc
    SEK: 'sv-SE', // Swedish Krona
    NOK: 'nb-NO', // Norwegian Krone
    DKK: 'da-DK', // Danish Krone
    PLN: 'pl-PL', // Polish Zloty
    CZK: 'cs-CZ', // Czech Koruna
    HUF: 'hu-HU', // Hungarian Forint
    RON: 'ro-RO', // Romanian Leu
    BGN: 'bg-BG', // Bulgarian Lev
    HRK: 'hr-HR', // Croatian Kuna

    // Asia-Pacific currencies
    AUD: 'en-AU', // Australian Dollar
    NZD: 'en-NZ', // New Zealand Dollar
    CAD: 'en-CA', // Canadian Dollar
    SGD: 'en-SG', // Singapore Dollar
    HKD: 'zh-HK', // Hong Kong Dollar
    TWD: 'zh-TW', // Taiwan Dollar
    KRW: 'ko-KR', // South Korean Won
    THB: 'th-TH', // Thai Baht
    MYR: 'ms-MY', // Malaysian Ringgit
    PHP: 'en-PH', // Philippine Peso
    VND: 'en-US', // Vietnamese Dong

    // Middle East & Africa
    AED: 'ar-AE', // UAE Dirham
    SAR: 'ar-SA', // Saudi Riyal
    QAR: 'ar-QA', // Qatari Riyal
    KWD: 'ar-KW', // Kuwaiti Dinar
    BHD: 'ar-BH', // Bahraini Dinar
    OMR: 'ar-OM', // Omani Rial
    JOD: 'ar-JO', // Jordanian Dinar
    LBP: 'ar-LB', // Lebanese Pound
    EGP: 'ar-EG', // Egyptian Pound
    ZAR: 'en-ZA', // South African Rand
    NGN: 'en-NG', // Nigerian Naira
    KES: 'en-KE', // Kenyan Shilling
    GHS: 'en-GH', // Ghanaian Cedi

    // Latin America
    BRL: 'pt-BR', // Brazilian Real
    MXN: 'es-MX', // Mexican Peso
    ARS: 'es-AR', // Argentine Peso
    CLP: 'es-CL', // Chilean Peso
    COP: 'es-CO', // Colombian Peso
    PEN: 'es-PE', // Peruvian Sol
    UYU: 'es-UY', // Uruguayan Peso
    BOB: 'es-BO', // Bolivian Boliviano
    PYG: 'es-PY', // Paraguayan Guarani

    // Other currencies
    RUB: 'ru-RU', // Russian Ruble
    UAH: 'uk-UA', // Ukrainian Hryvnia
    TRY: 'tr-TR', // Turkish Lira
    ILS: 'he-IL', // Israeli Shekel
    ISK: 'is-IS', // Icelandic Krona
}

/**
 * Alternative locale mappings for some currencies
 * Some currencies are used in multiple countries with different locales
 */
const ALTERNATIVE_LOCALE_MAP = {
    EUR: ['de-DE', 'fr-FR', 'it-IT', 'es-ES', 'nl-NL', 'pt-PT', 'fi-FI', 'el-GR'],
    USD: ['en-US', 'en-PR'], // Puerto Rico also uses USD
    CHF: ['de-CH', 'fr-CH', 'it-CH'], // Switzerland has multiple official languages
    CAD: ['en-CA', 'fr-CA'], // Canada has English and French
}

/**
 * Get locale for a given currency code
 *
 * Locale Handling Strategy:
 * 1. If userLocale is provided, use it (e.g., from user preferences or backend config)
 * 2. Otherwise, use the default currency-to-locale mapping
 * 3. Fall back to fallbackLocale if currency is not found
 *
 * This approach allows for:
 * - Multi-region currencies (e.g., INR used across India with different locales)
 * - User language preferences (e.g., Vietnamese user viewing VND should see Vietnam formatting)
 * - Backend-driven locale configuration
 *
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {string} [fallbackLocale='en-US'] - Fallback locale if currency not found
 * @param {string} [userLocale] - Optional user-preferred locale to override default mapping
 * @returns {string} BCP 47 language tag
 */
export function getLocaleForCurrency(currencyCode, fallbackLocale = 'en-US', userLocale = null) {
    // If userLocale is provided and valid, use it (allows translation/user preference override)
    if (userLocale && typeof userLocale === 'string' && userLocale.match(/^[a-z]{2}-[A-Z]{2}$/)) {
        return userLocale
    }
    if (!currencyCode || typeof currencyCode !== 'string') {
        console.warn('Invalid currency code provided, using fallback locale')
        return fallbackLocale
    }

    const upperCurrencyCode = currencyCode.toUpperCase()

    if (!(upperCurrencyCode in CURRENCY_LOCALE_MAP)) {
        console.warn(`No locale mapping found for currency: ${upperCurrencyCode}, using fallback locale`)
        return fallbackLocale
    }

    return CURRENCY_LOCALE_MAP[String(upperCurrencyCode)]
}

/**
 * Get all possible locales for a currency (useful for currencies used in multiple regions)
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string[]} Array of BCP 47 language tags
 */
export function getAllLocalesForCurrency(currencyCode) {
    if (!currencyCode || typeof currencyCode !== 'string') {
        return []
    }

    const upperCurrencyCode = currencyCode.toUpperCase()

    if (!(upperCurrencyCode in CURRENCY_LOCALE_MAP)) {
        return []
    }

    const primaryLocale = CURRENCY_LOCALE_MAP[String(upperCurrencyCode)]
    const alternativeLocales = ALTERNATIVE_LOCALE_MAP[String(upperCurrencyCode)]

    return alternativeLocales || [primaryLocale]
}

/**
 * Check if a currency code is supported
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {boolean} True if currency is supported
 */
export function isCurrencySupported(currencyCode) {
    if (!currencyCode || typeof currencyCode !== 'string') {
        return false
    }

    return Object.prototype.hasOwnProperty.call(CURRENCY_LOCALE_MAP, currencyCode.toUpperCase())
}

/**
 * Get all supported currency codes
 * @returns {string[]} Array of supported currency codes
 */
export function getSupportedCurrencies() {
    return Object.keys(CURRENCY_LOCALE_MAP)
}

/**
 * Format currency amount with proper locale and currency settings
 *
 * Locale Priority:
 * 1. userLocale (from options) - optional override
 * 2. Currency-based locale mapping
 * 3. fallbackLocale - default 'en-US'
 *
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {Object} options - Additional formatting options
 * @param {string} [options.fallbackLocale='en-US'] - Fallback locale if currency not found
 * @param {string} [options.fallbackCurrency='USD'] - Fallback currency if invalid
 * @param {string} [options.currencyDisplay='code'] - How to display currency (code, symbol, name)
 * @param {string} [options.userLocale] - Optional locale override
 * @returns {string} Formatted currency string
 */
export function formatCurrencyAmount(amount, currencyCode, options = {}) {
    const { fallbackLocale = 'en-US', fallbackCurrency = 'USD', currencyDisplay = 'code', userLocale, ...formatOptions } = options

    // Validate and get locale (userLocale takes precedence if provided)
    const locale = getLocaleForCurrency(currencyCode, fallbackLocale, userLocale)

    // Validate currency code
    let validCurrencyCode = currencyCode
    try {
        // Test if the currency code is valid by creating a NumberFormat
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
        })
    } catch (error) {
        console.warn(`Invalid currency code: ${currencyCode}, using fallback: ${fallbackCurrency}`)
        validCurrencyCode = fallbackCurrency
    }

    // Set appropriate fraction digits based on currency
    const defaultFractionDigits = ['IDR', 'JPY', 'KRW', 'VND'].includes(validCurrencyCode) ? 0 : 2

    const formatConfig = {
        currency: validCurrencyCode,
        currencyDisplay,
        minimumFractionDigits: defaultFractionDigits,
        maximumFractionDigits: defaultFractionDigits,
        ...formatOptions,
    }

    return new Intl.NumberFormat(locale, formatConfig).format(amount)
}

// Export the mapping objects for testing purposes
export { CURRENCY_LOCALE_MAP, ALTERNATIVE_LOCALE_MAP }
